import { api } from '@/lib/api-client';

import { translationsQueryKey } from './queryKeys';
import {
  DEFAULT_LOCALE,
  type GetTranslationLanguagesResponse,
  type LanguageOption,
} from '../models/translation';

export const getTranslationLanguages = async (): Promise<LanguageOption[]> => {
  const response = await api.get<GetTranslationLanguagesResponse>(
    `/v1/languages`,
  );
  // Default comes from the server; fall back to the SPA base locale when the
  // scope has none set yet. The default locale is always present in the list.
  const { languages, default_language } = response.data;
  const defaultLocale = default_language || DEFAULT_LOCALE;
  const percentByLocale = new Map(
    languages.map((language) => [
      language.language,
      language.translated_percent,
    ]),
  );
  const tags = percentByLocale.has(defaultLocale)
    ? [...percentByLocale.keys()]
    : [defaultLocale, ...percentByLocale.keys()];
  return tags.map((locale) => ({
    locale,
    default: locale === defaultLocale,
    selected: locale === defaultLocale,
    // The synthetic fallback default isn't in the response; treat it as fully
    // covered — it's already the (disabled) default, so 100 keeps it safe.
    translatedPercent: percentByLocale.get(locale) ?? 100,
  }));
};

/**
 * Loaded via a route loader (`ensureQueryData`), so the key must carry the
 * game/sandbox scope explicitly — accessible locales differ per sandbox and
 * this mirrors the key shape `useAppQuery` would produce. The fetch itself
 * needs no scope args: the axios client injects `game_code`/`sandbox_code`.
 */
export const getTranslationLanguagesQuery = (
  gameCode: string,
  sandboxCode: string,
) => ({
  queryKey: [gameCode, sandboxCode, ...translationsQueryKey.languages()],
  queryFn: getTranslationLanguages,
});
